import React, { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { MoreVertical, Send, Phone, Eye, Plus } from "lucide-react";
import {
  UnifiedCrmRecord,
  getDisplayName,
  getFullName,
  getInitials as getUnifiedInitials,
} from "../../../components/crm/all-leads/unified-crm-service";
import { ContactItem } from "../../crm-pages/customers/types";
import { TeamDetails } from "../../../components/crm/customer/types";

// ─────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────

export type Temperature = "hot" | "warm" | "cold" | "new";

export interface LeadRecord {
  id: string;
  name: string;
  display_name: string;
  initials: string;
  phone: string | null;
  email: string | null;
  tags: string[];
  status: string | null;
  channel: string | null;
  temperature: Temperature;
  score: number;
  updatedAt: string | null;
  source: string | null;
  // Not a confirmed backend field — stored under `details.custom.assigned_to`
  // as a best-effort guess until backend confirms the real shape.
  assignedTo: string | null;
  raw: UnifiedCrmRecord;
}

// ─────────────────────────────────────────────────────────────
// Data mapping — only real fields from the unified CRM record.
// Contacted/Converted and channel are best-effort derivations from real
// fields (status/tags/source), never invented. Fields with no real source
// (Last Message, week-over-week deltas) are shown as "—" rather than faked.
// ─────────────────────────────────────────────────────────────

export const TEMPERATURE_STYLES: Record<Temperature, { bg: string; text: string; label: string }> = {
  hot: { bg: "bg-amber-400", text: "text-gray-900", label: "Hot" },
  warm: { bg: "bg-emerald-500", text: "text-white", label: "Warm" },
  cold: { bg: "bg-sky-500", text: "text-white", label: "Cold" },
  new: { bg: "bg-gray-300", text: "text-gray-900", label: "New" },
};

export const STATUS_STYLES: Record<string, { bg: string; text: string; dot: string }> = {
  hot: { bg: "bg-amber-400", text: "text-gray-900", dot: "bg-gray-900" },
  warm: { bg: "bg-lime-500", text: "text-white", dot: "bg-white" },
  cold: { bg: "bg-sky-500", text: "text-white", dot: "bg-white" },
  contacted: { bg: "bg-blue-500", text: "text-white", dot: "bg-white" },
  converted: { bg: "bg-teal-500", text: "text-white", dot: "bg-white" },
  qualified: { bg: "bg-green-100", text: "text-green-800", dot: "bg-green-600" },
  "new lead": { bg: "bg-amber-400", text: "text-gray-900", dot: "bg-gray-900" },
  "not interested": { bg: "bg-red-500", text: "text-white", dot: "bg-white" },
};
export const DEFAULT_STATUS_STYLE = { bg: "bg-gray-200", text: "text-gray-700", dot: "bg-gray-500" };

export const CHANNEL_STYLES: Record<string, string> = {
  whatsapp: "text-green-600",
  linkedin: "text-blue-600",
  facebook: "text-blue-600",
  instagram: "text-pink-600",
  sms: "text-purple-600",
  email: "text-amber-600",
  rcs: "text-indigo-600",
};

export const determineTemperature = (record: UnifiedCrmRecord): Temperature => {
  const temp = (record.details?.custom?.temperature as string)?.toLowerCase();
  if (temp === "hot" || temp === "warm" || temp === "cold") return temp;
  const score = (record.details?.score as number) || 0;
  if (score >= 80) return "hot";
  if (score >= 50) return "warm";
  if (score > 0) return "cold";
  return "new";
};

// Best-effort channel from real fields — never a fabricated default.
export const inferChannel = (record: UnifiedCrmRecord): string | null => {
  const source = (record.details?.source || "").toLowerCase();
  for (const key of Object.keys(CHANNEL_STYLES)) {
    if (source.includes(key)) return key;
  }
  const rawTags = record.details?.tags;
  const tags = Array.isArray(rawTags) ? rawTags.map((t) => t.toLowerCase()) : [];
  for (const key of Object.keys(CHANNEL_STYLES)) {
    if (tags.some((t) => t.includes(key))) return key;
  }
  return null;
};

export const getTags = (record: UnifiedCrmRecord): string[] => {
  const rawTags = record.details?.tags as unknown;
  if (Array.isArray(rawTags)) return rawTags as string[];
  if (typeof rawTags === "string" && rawTags.trim()) return [rawTags];
  return [];
};

export const mapRecordToLead = (record: UnifiedCrmRecord): LeadRecord => ({
  id: record.id,
  name: getFullName(record),
  display_name: getDisplayName(record),
  initials: getUnifiedInitials(record),
  phone: record.phone,
  email: record.email,
  tags: getTags(record),
  status: record.status,
  channel: inferChannel(record),
  temperature: determineTemperature(record),
  score: (record.details?.score as number) || 0,
  updatedAt: record.updated_at,
  source: record.details?.source || null,
  assignedTo: (record.details?.custom?.assigned_to as string) || null,
  raw: record,
});

export const recordToContactItem = (lead: LeadRecord): ContactItem => {
  const [firstName, ...rest] = lead.name.split(" ");
  return {
    id: lead.id,
    email: lead.email || "",
    phone: lead.phone || "",
    first_name: firstName || lead.name,
    last_name: rest.join(" "),
    status: lead.status || undefined,
    tags: lead.tags,
    source: lead.source,
    score: lead.score,
    custom: lead.raw.details?.custom as ContactItem["custom"],
  };
};

// ─────────────────────────────────────────────────────────────
// Small UI atoms shared between the Recent Leads dashboard and the
// All Leads directory page.
// ─────────────────────────────────────────────────────────────

export const StatusPill: React.FC<{ status: string | null }> = ({ status }) => {
  if (!status) return <span className="text-xs text-[#7B7B7B] font-semibold dark:text-gray-500">—</span>;
  const style = STATUS_STYLES[status.toLowerCase()] || DEFAULT_STATUS_STYLE;
  return (
    <span className={`inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full ${style.bg} ${style.text}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${style.dot}`} />
      {status}
    </span>
  );
};

// Deal Status — driven purely by assignment, not the lead's backend status:
// unassigned leads are always "New Lead", assigned leads are "Contacted".
export const DealStatusPill: React.FC<{ assignedTo: string | null }> = ({ assignedTo }) => {
  const assigned = !!assignedTo;
  return (
    <span
      className={`inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full ${
        assigned ? "bg-indigo-50 dark:bg-indigo-500/15 text-indigo-700 dark:text-indigo-400" : "bg-[#FFDF3D] text-gray-900"
      }`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${assigned ? "bg-indigo-600" : "bg-gray-900"}`} />
      {assigned ? "Contacted" : "New Lead"}
    </span>
  );
};

// Raw source values like "Inbound_Conversation" come straight from the
// backend's internal enum — display copy should read "Inbound Conversation".
export const formatSourceLabel = (source: string | null | undefined): string => {
  if (!source) return "";
  return source.replace(/_/g, " ");
};

export const ChannelLabel: React.FC<{ channel: string | null }> = ({ channel }) => {
  if (!channel) return <span className="text-xs text-[#7B7B7B] font-semibold dark:text-gray-500">—</span>;
  const color = CHANNEL_STYLES[channel] || "text-gray-600";
  return <span className={`text-xs font-medium capitalize ${color}`}>{channel}</span>;
};

const CRM_BASE_URL = process.env.REACT_APP_BACKEND_URL || "https://api.crm.dalmlabs.com/api/v1";

// The full record from GET /leads/{leadId} — a different, richer shape than
// the /crm/unified list row (LeadRecord.raw). This is the only place
// `conversation_id`, `custom`, and the real `score`/`status` live.
export interface FullLead {
  id: string;
  email: string | null;
  first_name: string | null;
  last_name: string | null;
  phone: string | null;
  title: string | null;
  display_name: string | null;
  conversation_id: string | null;
  external_thread_id: string | null;
  source: string | null;
  score: number;
  status: string;
  tags: string[];
  custom: Record<string, any>;
  updated_at: string;
}

export const fetchFullLead = async (leadId: string, token: string | null): Promise<FullLead | null> => {
  try {
    const res = await fetch(`${CRM_BASE_URL}/leads/${encodeURIComponent(leadId)}`, {
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
    });
    if (!res.ok) return null;
    return res.json();
  } catch {
    return null;
  }
};

export type LeadUpdatePatch = Partial<{
  email: string | null;
  first_name: string | null;
  last_name: string | null;
  phone: string | null;
  title: string | null;
  display_name: string | null;
  source: string | null;
  score: number;
  status: string;
  tags: string[];
  custom: Record<string, any>;
}>;

// PUT /leads/{id} is a full replacement, not a patch — any field left out of
// the body resets to its default (score -> 0, status -> "new", tags -> [],
// custom -> {}, everything else -> null). So every write here fetches the
// latest lead first and sends the complete record back, merged with only the
// fields actually being changed — never a partial object built from stale
// list-row data.
export const updateLeadSafely = async (
  leadId: string,
  patch: LeadUpdatePatch,
  token: string | null,
): Promise<{ ok: boolean; lead?: FullLead; error?: string }> => {
  const current = await fetchFullLead(leadId, token);
  if (!current) return { ok: false, error: "Could not load the latest lead record" };

  const payload = {
    email: patch.email !== undefined ? patch.email : current.email,
    first_name: patch.first_name !== undefined ? patch.first_name : current.first_name,
    last_name: patch.last_name !== undefined ? patch.last_name : current.last_name,
    phone: patch.phone !== undefined ? patch.phone : current.phone,
    title: patch.title !== undefined ? patch.title : current.title,
    display_name: patch.display_name !== undefined ? patch.display_name : current.display_name,
    conversation_id: current.conversation_id,
    external_thread_id: current.external_thread_id,
    source: patch.source !== undefined ? patch.source : current.source,
    score: patch.score !== undefined ? patch.score : current.score,
    status: patch.status !== undefined ? patch.status : current.status,
    tags: patch.tags !== undefined ? patch.tags : current.tags,
    custom: patch.custom !== undefined ? { ...current.custom, ...patch.custom } : current.custom,
  };

  if (!payload.email?.trim() && !payload.phone?.trim()) {
    return { ok: false, error: "A lead must retain either an email address or phone number" };
  }

  try {
    const res = await fetch(`${CRM_BASE_URL}/leads/${encodeURIComponent(leadId)}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify(payload),
    });
    const json = await res.json().catch(() => null);
    if (!res.ok) return { ok: false, error: json?.detail || json?.message || "Failed to update lead" };
    return { ok: true, lead: json };
  } catch {
    return { ok: false, error: "Failed to update lead" };
  }
};

const digitsOnly = (value?: string | null): string => String(value || "").replace(/\D/g, "");

// GET /conversations only returns active (non-archived) conversations, so a
// phone match here misses anything archived even though it still exists —
// that's the "start conversation does nothing" bug: the lead's conversation
// was real, just archived, and the inbox's own search silently excluded it.
const findActiveConversationByPhone = async (phone: string, token: string | null) => {
  const res = await fetch(`${CRM_BASE_URL}/conversations?${new URLSearchParams({ search: phone })}`, {
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
  });
  if (!res.ok) return null;
  const data = await res.json();
  const list: any[] = Array.isArray(data) ? data : data?.results || [];
  const target = digitsOnly(phone);
  return (
    list.find((conv) => {
      const threadPhone = digitsOnly(conv.external_thread_id);
      const titlePhone = digitsOnly(conv.title);
      return (
        (!!threadPhone && (threadPhone === target || threadPhone.endsWith(target) || target.endsWith(threadPhone))) ||
        (!!titlePhone && titlePhone.includes(target))
      );
    }) || null
  );
};

const findArchivedConversationByPhone = async (phone: string, token: string | null) => {
  const res = await fetch(`${CRM_BASE_URL}/conversations/archive?${new URLSearchParams({ search: phone })}`, {
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
  });
  if (!res.ok) return null;
  const data = await res.json();
  const list: any[] = Array.isArray(data) ? data : data?.results || [];
  const target = digitsOnly(phone);
  return list.find((conv) => digitsOnly(conv.contact?.phone) === target) || null;
};

// Same "restore then jump to the inbox and select it" flow the Archive
// screen's own "Resume Conversation" button uses — a lead's conversation
// being archived shouldn't mean "Start Conversation" silently does nothing.
export const goToLeadConversation = async (navigate: ReturnType<typeof useNavigate>, lead: LeadRecord) => {
  if (!lead.phone) {
    toast.error("This lead has no phone number on file yet.");
    return;
  }

  const token = localStorage.getItem("authToken");

  try {
    // Prefer the real, exact link over the phone-search heuristics below —
    // those hit a bare GET /conversations list search that isn't a reliable/
    // registered route on this backend. Some tenants' /crm/unified list rows
    // already carry conversation_id (details.conversation_id); others only
    // have it on the separate GET /leads/{id} record. Check both.
    const listConversationId = (lead.raw?.details as any)?.conversation_id as string | undefined;
    const fullLead = listConversationId ? null : await fetchFullLead(lead.id, token);
    const linkedConversationId = listConversationId || fullLead?.conversation_id;
    if (linkedConversationId) {
      const convId = linkedConversationId;
      const direct = await fetch(`${CRM_BASE_URL}/conversations/${encodeURIComponent(convId)}`, {
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      });
      if (direct.ok) {
        sessionStorage.setItem(
          "pendingConversationTarget",
          JSON.stringify({ conversationId: convId, phoneNumber: lead.phone, title: lead.name }),
        );
        navigate("/chats");
        return;
      }

      // Not found active — it may be archived under the same id.
      const unarchiveRes = await fetch(`${CRM_BASE_URL}/conversations/${encodeURIComponent(convId)}/unarchive`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      });
      if (unarchiveRes.ok) {
        sessionStorage.setItem(
          "pendingConversationTarget",
          JSON.stringify({ conversationId: convId, phoneNumber: lead.phone, title: lead.name }),
        );
        navigate("/chats");
        return;
      }
    }

    const active = await findActiveConversationByPhone(lead.phone, token);
    if (active?.id) {
      sessionStorage.setItem(
        "pendingConversationTarget",
        JSON.stringify({ conversationId: active.id, phoneNumber: lead.phone, title: lead.name }),
      );
      navigate("/chats");
      return;
    }

    const archived = await findArchivedConversationByPhone(lead.phone, token);
    if (archived?.id) {
      const res = await fetch(`${CRM_BASE_URL}/conversations/${archived.id}/unarchive`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        toast.error("Failed to resume conversation");
        return;
      }
      sessionStorage.setItem(
        "pendingConversationTarget",
        JSON.stringify({ conversationId: archived.id, phoneNumber: lead.phone, title: lead.name }),
      );
      navigate("/chats");
      return;
    }
  } catch {
    // Lookup failed (network/etc) — fall through to the plain phone/title
    // target below so /chats can still open a fresh compose.
  }

  sessionStorage.setItem(
    "pendingConversationTarget",
    JSON.stringify({ phoneNumber: lead.phone, title: lead.name }),
  );
  navigate("/chats");
};

export const LeadTableRow: React.FC<{
  lead: LeadRecord;
  onView: () => void;
  onEdit: () => void;
  onOpenDetails: (initialTab: "Details" | "Insight" | "Actions") => void;
  onManageTags: (lead: LeadRecord) => void;
  teamMembers: TeamDetails[];
  onOpenAssign: (lead: LeadRecord) => void;
}> = ({ lead, onView, onOpenDetails, onManageTags, teamMembers, onOpenAssign }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuOpensUpward, setMenuOpensUpward] = useState(false);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const assignedMember = teamMembers.find((m) => m.id === lead.assignedTo) || null;

  // Rows near the bottom of the viewport (e.g. the last row of the table)
  // would otherwise render this dropdown below the fold, invisible until
  // the page is scrolled — flip it to open upward when there isn't enough
  // room below the button.
  const toggleMenu = () => {
    if (!menuOpen && menuButtonRef.current) {
      const rect = menuButtonRef.current.getBoundingClientRect();
      const estimatedMenuHeight = 140;
      setMenuOpensUpward(window.innerHeight - rect.bottom < estimatedMenuHeight);
    }
    setMenuOpen((o) => !o);
  };

  const handleCallContact = () => {
    setMenuOpen(false);
    if (!lead.phone) {
      toast.error("This lead has no phone number on file yet.");
      return;
    }
    window.location.href = `tel:${lead.phone}`;
  };

  return (
    <tr className="border-b border-gray-50 dark:border-gray-800 hover:bg-gray-50/60 dark:hover:bg-gray-800/60 transition-colors">
      <td className="py-3 px-2">
        <button className="flex items-center gap-2.5 bg-transparent border-none cursor-pointer p-0 text-left">
          <div className="w-8 h-8 rounded-full bg-indigo-100 dark:bg-indigo-500/20 flex items-center justify-center text-indigo-600 dark:text-indigo-400 text-[11px] font-semibold shrink-0">
            {lead.initials}
          </div>
          <span className="text-sm font-medium text-gray-800 dark:text-gray-200 hover:text-indigo-600 dark:hover:text-indigo-400">{lead.display_name}</span>
        </button>
      </td>
      <td className="py-3 px-2 text-sm text-gray-600 dark:text-gray-400 whitespace-nowrap">{lead.phone || "—"}</td>
      <td className="py-3 px-2 text-sm text-gray-600 dark:text-gray-400 whitespace-nowrap capitalize">{formatSourceLabel(lead.source) || "—"}</td>
      <td className="py-3 px-2">
        {lead.tags.length === 0 ? (
          <button
            onClick={() => onManageTags(lead)}
            title="Add a tag"
            className="text-xs text-[#7B7B7B] font-semibold dark:text-gray-500 bg-transparent border-none cursor-pointer p-0 hover:text-indigo-600 dark:hover:text-indigo-400"
          >
            + Add tag
          </button>
        ) : (
          <div className="flex items-center gap-1 flex-wrap max-w-[220px]">
            {lead.tags.slice(0, 2).map((t) => (
              <button
                key={t}
                onClick={() => onManageTags(lead)}
                title="View and manage tags"
                className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 border-none cursor-pointer hover:bg-indigo-100 dark:hover:bg-indigo-500/20 hover:text-indigo-700 dark:hover:text-indigo-400 transition-colors"
              >
                {t}
              </button>
            ))}
            {lead.tags.length > 2 && (
              <button
                onClick={() => onManageTags(lead)}
                className="text-[11px] text-[#7B7B7B] font-semibold dark:text-gray-500 bg-transparent border-none cursor-pointer p-0 hover:text-indigo-600 dark:hover:text-indigo-400"
              >
                +{lead.tags.length - 2} more
              </button>
            )}
          </div>
        )}
      </td>
      <td className="py-3 px-2"><DealStatusPill assignedTo={lead.assignedTo} /></td>
      <td className="py-3 px-2">
        {assignedMember ? (
          <button
            onClick={() => onOpenAssign(lead)}
            className="text-sm font-medium text-[#4338CA] dark:text-indigo-400 underline decoration-gray-400 underline-offset-2 hover:text-indigo-600 dark:hover:text-indigo-300 hover:decoration-indigo-400 bg-transparent border-none cursor-pointer p-0"
          >
            {`${assignedMember.first_name} ${assignedMember.last_name}`.trim()}
          </button>
        ) : (
          <button
            onClick={() => onOpenAssign(lead)}
            className="flex items-center gap-1 text-xs font-medium text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-700 rounded-lg px-2.5 py-1.5 hover:bg-gray-50 dark:hover:bg-gray-800 bg-white dark:bg-gray-900 cursor-pointer transition-colors"
          >
            <Plus size={12} />
            Assign Agent
          </button>
        )}
      </td>
      <td className="py-3 px-2 relative">
        <button
          ref={menuButtonRef}
          onClick={toggleMenu}
          className="p-1.5 rounded-lg text-[#7B7B7B] font-semibold dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 bg-transparent border-none cursor-pointer"
        >
          <MoreVertical size={16} />
        </button>
        {menuOpen && (
          <>
            <div className="fixed inset-0 z-10" onClick={() => setMenuOpen(false)} />
            <div
              className={`absolute right-2 ${menuOpensUpward ? "bottom-9" : "top-9"} z-20 w-44 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg overflow-hidden py-1`}
            >
              <button
                onClick={() => { setMenuOpen(false); onView(); }}
                className="w-full flex items-center gap-2 text-left px-3.5 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 bg-white dark:bg-gray-900 border-none cursor-pointer"
              >
                <Send size={14} className="text-[#7B7B7B] font-semibold dark:text-gray-500" />
                Send Message
              </button>
              <button
                onClick={handleCallContact}
                className="w-full flex items-center gap-2 text-left px-3.5 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 bg-white dark:bg-gray-900 border-none cursor-pointer"
              >
                <Phone size={14} className="text-[#7B7B7B] font-semibold dark:text-gray-500" />
                Call Contact
              </button>
              <button
                onClick={() => { setMenuOpen(false); onOpenDetails("Actions"); }}
                className="w-full flex items-center gap-2 text-left px-3.5 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 bg-white dark:bg-gray-900 border-none cursor-pointer"
              >
                <Eye size={14} className="text-[#7B7B7B] font-semibold dark:text-gray-500" />
                View Lead Details
              </button>
            </div>
          </>
        )}
      </td>
    </tr>
  );
};
